// frontend/src/providers/store.js

import { configureStore } from "@reduxjs/toolkit";
import tutorialReducer from "src/slices/tutorialSlice";
import counterReducer from "src/slices/counterSlice";
import baselineReducer from "src/slices/baselineSlice";
import scenarioReducer from "src/slices/scenarioSlice";
import userReducer from "src/slices/userSlice";
import userGroupReducer from "src/slices/userGroupSlice";
import logReducer from "src/slices/logSlice";
import auctionReducer from "src/slices/auctionSlice";
import appReducer from "src/slices/appSlice";
import sessionReducer from "src/slices/sessionSlice";
import configsReducer from "src/slices/configsSlice";
import { debugLog, errorLog } from "src/utils/debugUtils";

const reducers = {
	tutorials: tutorialReducer,
	counter: counterReducer,
	users: userReducer,
	userGroups: userGroupReducer,
	baselines: baselineReducer,
	scenarios: scenarioReducer,
	logs: logReducer,
	auctions: auctionReducer,
	app: appReducer,
	session: sessionReducer,
	configs: configsReducer,
};

// --- Rehydrate session state on app startup ---
// This restores loginUser and ip from sessionStorage
// so Redux still has them after a page reload or MSAL redirect.
let preloadedSession = undefined;
try {
	//  Properly restore both loginUser and currUser from sessionStorage
	const savedLoginUser = JSON.parse(sessionStorage.getItem("loginUser") || "{}");
	const savedCurrUser = JSON.parse(sessionStorage.getItem("currUser") || "{}");
	const savedIP = JSON.parse(sessionStorage.getItem("ip") || '""');

	// Only preload if there’s something useful saved
	if (Object.keys(savedLoginUser).length > 0 || savedIP) {
		preloadedSession = {
			session: {
				// Acting as self by default
				currUser: Object.keys(savedCurrUser).length > 0 ? savedCurrUser : savedLoginUser, // fallback to self
				// True login identity
				loginUser: savedLoginUser,
				ip: savedIP,
				status: {
					create: "idle",
					retrieve: "idle",
					retrieveOne: "idle",
					update: "idle",
					find: "idle",
				},
				error: {
					create: null,
					retrieve: null,
					retrieveOne: null,
					update: null,
					find: null,
				},
			},
		};

		debugLog(3, "[store][STATE] preloaded session from sessionStorage", {
			session: preloadedSession.session,
		});
	}
} catch (e) {
	console.warn("[store] session rehydration failed:", e);
	errorLog(2, "[store][FLOW] session rehydration failed", { error: e.message });
}

// Ensure we don't stringify non-plain objects
const safeStringify = (obj) => {
	try {
		return JSON.stringify(obj);
	} catch (e) {
		console.warn("Session stringify failed:", e);
		return "{}";
	}
};

const sanitizeReducers = (reducers) =>
	Object.fromEntries(Object.entries(reducers).filter(([, reducer]) => typeof reducer === "function"));

// Persist session state to sessionStorage
const persistSession = (store) => (next) => (action) => {
	const result = next(action);
	if (process.env.NODE_ENV === "test") return result; // prevent Jest from accidentally writing to sessionStorage

	const state = store.getState().session;
	try {
		// Selective persistence — only save chosen keys
		const keysToPersist = ["loginUser", "currUser", "ip"];
		keysToPersist.forEach((key) => {
			sessionStorage.setItem(key, safeStringify(state[key]));
		});

		// Keep infra logging low-noise: only log for session actions in dev.
		if (process.env.NODE_ENV !== "production") {
			if (action.type?.startsWith("session/")) {
				debugLog(2, "[store][FLOW] persistSession executed", {
					keys: keysToPersist,
					loginUserId: state.loginUser?.id,
					ip: state.ip,
					actionType: action.type,
					module: "store",
				});
			}
		}
	} catch (e) {
		console.warn("Failed to persist session:", e);
		errorLog(2, "[store][FLOW] persistSession failed", {
			error: e.message,
			module: "store",
			actionType: action.type,
		});
	}

	return result;
};

const store = configureStore({
	// reducer: { ...reducers },
	// Add reducer sanitization BEFORE configureStore
	// Reducer sanitization fixes the error like "No reducer provided for key "users"
	// because Redux only errors when a reducer key exists with an undefined value,
	// and sanitization removes those invalid keys before Redux evaluates them.
	reducer: sanitizeReducers(reducers),
	devTools: true,

	// Hydrate Redux from persisted session data
	preloadedState: preloadedSession,

	// Persist session state to sessionStorage
	// persistSession: persistence by middleware is automatic, always-on, identity persistence
	// NOTE: UI preferences (configs) are NOT persisted here.
	// They persist themselves inside configsSlice reducers.
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			thunk: true,
			serializableCheck: false,
			immutableCheck: false,
		}).concat(persistSession),
});

export default store;
export const rootReducer = reducers;
export const RootState = () => store.getState();
